import React from 'react';
import { Users, Target, Award, TrendingUp, Heart, Shield } from 'lucide-react';
import Header from './components/Header9';
import Footer from './components/Footer9';

const About = () => {
    const stats = [
        { number: '500+', label: 'Artisan Partners' },
        { number: '10,000+', label: 'Happy Customers' },
        { number: '25+', label: 'States Covered' },
        { number: '50+', label: 'Weaving Traditions' },
    ];

    const values = [
        { icon: Heart, title: 'Heritage First', description: 'We preserve and promote traditional Indian textile techniques that have been perfected over centuries.' },
        { icon: Shield, title: 'Quality Assured', description: 'Every piece undergoes rigorous quality checks ensuring only the finest craftsmanship reaches you.' },
        { icon: Users, title: 'Community Impact', description: 'We work directly with artisan communities, ensuring fair wages and sustainable livelihoods.' },
        { icon: TrendingUp, title: 'Modern Vision', description: 'We bridge the gap between ancient craft and contemporary design, making heritage relevant today.' },
    ];

    const team = [
        { name: 'Ananya Kapoor', role: 'Founder & Creative Director', image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Vikram Sharma', role: 'Head of Operations', image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { name: 'Meera Patel', role: 'Lead Designer', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            {/* Hero */}
            <div className="pt-20 md:pt-24 pb-8 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-0.5 bg-[#2563EB]" />
                        <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium">Our Story</p>
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">About NAKSHA</h1>
                </div>
            </div>

            {/* Mission Section */}
            <section className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="relative">
                            <img
                                src="https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=800"
                                alt="NAKSHA Craftsmanship"
                                className="w-full aspect-[4/5] object-cover rounded-none"
                            />
                            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#2563EB]" />
                            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#2563EB]" />
                        </div>
                        <div>
                            <p className="text-xs tracking-[0.3em] uppercase text-[#2563EB] font-medium mb-3">Our Mission</p>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A] mb-6">
                                Blueprint for the Future of Indian Fashion
                            </h2>
                            <div className="w-12 h-0.5 bg-[#2563EB] mb-6" />
                            <p className="text-sm sm:text-base text-[#64748B] leading-relaxed mb-4 font-light">
                                NAKSHA was born from a simple belief: that India's rich textile heritage deserves a modern platform. We are not just a fashion brand; we are a bridge between centuries-old craftsmanship and contemporary design sensibility.
                            </p>
                            <p className="text-sm sm:text-base text-[#64748B] leading-relaxed mb-4 font-light">
                                Every product in our collection is a collaboration between skilled artisans from India's textile heartlands and our design team. From the handloom weavers of Varanasi to the block printers of Jaipur, we work with communities that carry forward an invaluable legacy.
                            </p>
                            <p className="text-sm sm:text-base text-[#64748B] leading-relaxed font-light">
                                Our name, NAKSHA, means "blueprint" in Hindi --- a fitting description for our approach to fashion that maps traditional techniques onto modern silhouettes.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="py-12 sm:py-16 bg-[#F8FAFC] border-t border-b border-[#E2E8F0]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, idx) => (
                            <div key={idx} className="text-center">
                                <p className="text-3xl sm:text-4xl font-bold text-[#2563EB] mb-1">{stat.number}</p>
                                <p className="text-xs sm:text-sm text-[#64748B] font-light tracking-wider uppercase">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="py-16 sm:py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 md:mb-16">
                        <p className="text-xs sm:text-sm tracking-[0.3em] uppercase text-[#2563EB] font-medium mb-3">What We Stand For</p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0F172A] mb-4">Our Values</h2>
                        <div className="w-12 h-0.5 bg-[#2563EB] mx-auto" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map((value, idx) => (
                            <div key={idx} className="border border-[#E2E8F0] rounded-none p-6 relative hover:border-[#2563EB] transition-colors duration-300">
                                <div className="absolute top-0 left-0 w-1 h-full bg-[#2563EB]" />
                                <value.icon size={24} className="text-[#2563EB] mb-4" strokeWidth={1.5} />
                                <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider mb-3">{value.title}</h3>
                                <p className="text-sm text-[#64748B] leading-relaxed font-light">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Team */}
            <section className="py-16 sm:py-20 lg:py-28 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 md:mb-16">
                        <p className="text-xs sm:text-sm tracking-[0.3em] uppercase text-[#2563EB] font-medium mb-3">The People Behind NAKSHA</p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0F172A] mb-4">Our Team</h2>
                        <div className="w-12 h-0.5 bg-[#2563EB] mx-auto" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        {team.map((member, idx) => (
                            <div key={idx} className="text-center">
                                <div className="aspect-[3/4] overflow-hidden rounded-none border border-[#E2E8F0] mb-4 relative">
                                    <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                                    <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#2563EB]" />
                                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#2563EB]" />
                                </div>
                                <p className="text-sm font-semibold text-[#0F172A] mb-1">{member.name}</p>
                                <p className="text-xs text-[#64748B] font-light">{member.role}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default About;
